// src/api/client.js
import axios from "axios";
import { getAccessToken, getViewerEmail, clearSession } from "../utils/session";

// Base URL priority:
// 1) Vite env at build time (VITE_API_BASE_URL=http://127.0.0.1:8000/api)
// 2) window override at runtime (optional)
// 3) fallback to relative "/api" (for IIS reverse proxy)
const envBase =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  (typeof window !== "undefined" && window.VITE_API_BASE_URL) ||
  "/api";

// Strip trailing slash so we don't get /api//users etc.
const BASE_URL = envBase.replace(/\/+$/, "");

// Exported so modules that can't go through axios (EventSource/SSE,
// WebSocket — see mcp/api/mcp.js) can still build a URL against the same
// resolved base instead of hardcoding their own "/api/..." constant.
export { BASE_URL };

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 600000,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  const t = getAccessToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }

  // ✅ send viewer email for SFDC filtering
  const email = getViewerEmail();
  if (email) {
    cfg.headers = cfg.headers || {};
    cfg.headers["X-User-Email"] = email;
  }

  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const s = err?.response?.status;
    if (s === 401) {
      clearSession();
      if (
        typeof window !== "undefined" &&
        window.location.pathname !== "/login"
      ) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// ----------------------------------------------------
// Generic helpers (used across SOW Analyzer UI)
// ----------------------------------------------------

export function apiGet(path, config = {}) {
  return api.get(path, config).then((r) => r.data);
}

export function apiPost(path, body = {}, config = {}) {
  return api.post(path, body, config).then((r) => r.data);
}

export function apiPut(path, body = {}, config = {}) {
  return api.put(path, body, config).then((r) => r.data);
}

export function apiDelete(path, config = {}) {
  return api.delete(path, config).then((r) => r.data);
}

export function apiJson(path, body = {}, method = "POST", config = {}) {
  return api({
    url: path,
    method,
    data: body,
    ...config,
  }).then((r) => r.data);
}

export function apiForm(path, formData, config = {}) {
  return api
    .post(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...(config.headers || {}),
      },
      ...config,
    })
    .then((r) => r.data);
}

// ----------------------------------------------------
// Error helper
// ----------------------------------------------------

export function getApiError(err) {
  // The backend's own domain exceptions serialize as
  // HTTPException(detail=exc.to_dict()) -> {code, message, context,
  // correlation_id} under the `detail` key, not a plain string — and
  // FastAPI's own automatic Pydantic validation errors put a LIST of
  // {loc, msg, type} there instead. Returning either shape directly used to
  // hand callers an object/array instead of a string; setting that
  // straight into UI state and rendering it as a JSX child crashes with
  // "Objects are not valid as a React child" — exactly the generic
  // "error occurred in the <div> component" symptom, not obviously
  // connected to its real cause. Always resolve to an actual string here.
  const detail = err?.response?.data?.detail;
  let detailMsg;
  if (typeof detail === "string") {
    detailMsg = detail;
  } else if (Array.isArray(detail)) {
    detailMsg = detail[0]?.msg;
  } else if (detail && typeof detail === "object") {
    detailMsg = detail.message;
  }

  return (
    detailMsg ||
    err?.response?.data?.message ||
    err?.message ||
    "Request failed"
  );
}

// URL normalization (avoid /api/api/...)
// ----------------------------------------------------
function normalizeUrl(url) {
  let u = String(url || "");
  if (!u.startsWith("/")) u = "/" + u;
  const base = String(BASE_URL || "").toLowerCase().replace(/\/+$/, "");
  const uLower = u.toLowerCase();
  if (base.endsWith("/api") && uLower.startsWith("/api/")) {
    return u.slice(4);
  }
  return u;
}


// ----------------------------------------------------
// RAG KB HELPERS (Agentic RAG module)
// ----------------------------------------------------


export function ragListCollections(projectId) {
  return api
    .get("/kb/collections", {
      params: projectId ? { project_id: projectId } : undefined,
    })
    .then((r) => r.data);
}

export function ragCreateCollection(payload) {
  return api.post("/kb/collections", payload).then((r) => r.data);
}

export function ragUploadDocuments(
  collectionId,
  { files = [], serverPaths = "", urls = "" }
) {
  const formData = new FormData();
  (files || []).forEach((f) => formData.append("files", f));
  if (serverPaths) formData.append("server_paths", serverPaths);
  if (urls) formData.append("urls", urls);

  return api
    .post(`/kb/${collectionId}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000,
    })
    .then((r) => r.data);
}

export function ragBuildIndex(collectionId) {
  return ragStartIndex(collectionId);
}

export function ragSearch(collectionId, payload) {
  return api
    .post(`/search/collections/${collectionId}`, payload)
    .then((r) => r.data);
}

export function ragAsk(collectionId, projectId, payload) {
  return api
    .post(`/search/collections/${collectionId}/ask`, payload, {
      params: { project_id: projectId },
    })
    .then((r) => r.data);
}

export function ragAnalyticsOverview(collectionId) {
  return api
    .get(`/analytics/collections/${collectionId}/overview`)
    .then((r) => r.data);
}

export function ragAnalyticsRecent(collectionId, limit = 20) {
  return api
    .get(`/analytics/collections/${collectionId}/recent`, {
      params: { limit },
    })
    .then((r) => r.data);
}

export function ragStartIndex(collectionId) {
  return api
    .post(`/kb/${collectionId}/index/start`, {})
    .then((r) => r.data);
}

export function ragIndexProgress(collectionId) {
  return api
    .get(`/kb/${collectionId}/index/progress`)
    .then((r) => r.data);
}

export function ragListJobs(collectionId) {
  return api
    .get(`/kb/collections/${collectionId}/jobs`)
    .then((r) => r.data);
}

export function ragJobDetail(jobId) {
  return api.get(`/kb/jobs/${jobId}`).then((r) => r.data);
}

export function ragKbOverview() {
  return api.get("/kb/overview").then((r) => r.data);
}

export function ragListJobsForCollection(collectionId) {
  return api
    .get(`/kb/collections/${collectionId}/jobs`)
    .then((r) => r.data);
}


// ----------------------------------------------------
// SOW Template Builder API
// ----------------------------------------------------

export const sowTemplateApi = {
  list: (query = "", contractType = "", scope = "") => {
    const qs = [];
    if (query) qs.push(`search=${encodeURIComponent(query)}`);
    if (contractType) qs.push(`contract_type=${encodeURIComponent(contractType)}`);
    if (scope) qs.push(`scope=${encodeURIComponent(scope)}`);
    qs.push("page_size=50");
    return api.get(`/sow-templates${qs.length ? `?${qs.join("&")}` : ""}`).then((r) => r.data);
  },

  // Aliases for compatibility
  listTemplates: function(contractType = "") {
    return this.list("", contractType);
  },

  listTemplatesV2: function(query = "", contractType = "") {
    return this.list(query, contractType);
  },

  get: (id) => api.get(`/sow-templates/${id}`).then((r) => r.data),
  getTemplate: function(id) { return this.get(id); },

  create: (body) => api.post(`/sow-templates`, body).then((r) => r.data),
  createTemplate: function(body) { return this.create(body); },

  update: (id, body) => api.put(`/sow-templates/${id}`, body).then((r) => r.data),
  updateTemplate: function(id, body) { return this.update(id, body); },

  delete: (id) => api.delete(`/sow-templates/${id}`).then((r) => r.data),
  deleteTemplate: function(id) { return this.delete(id); },

  uploadDocx: (formData) =>
    api
      .post(`/sow-templates/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      })
      .then((r) => r.data),
  uploadTemplateDocx: function(formData) { return this.uploadDocx(formData); },

  downloadDocx: (id) =>
    api.get(`/sow-templates/${id}/download`, { responseType: "blob" }).then((r) => r.data),
  downloadTemplateDocx: function(id) { return this.downloadDocx(id); },
};

// ----------------------------------------------------
// SOW Draft API (Separate)
// ----------------------------------------------------

// Targets architecture_scaffold's Clean Architecture backend
// (presentation/sow/sow_draft_controller.py + sow_template_controller.py +
// opportunity_controller.py) — NOT the legacy app/ backend. Draft/template/
// opportunity ids are all plain integers there, same as here, but several
// shapes differ from what this file's functions returned before: no
// account_name/contract_type/project_start_date on an opportunity, no
// version_id (versions are keyed by a "major.minor" string), no
// current-version or salesforce-status endpoints, generation status is a
// single aggregate string (not current/total/section/percent progress).
// preview() is intentionally left pointing at the old, now-nonexistent
// /sow-drafts/{id}/preview route — no backend equivalent yet, revisit later.
export const sowDraftApi = {
  // Opportunities — page/page_size match the backend's pagination
  // contract; user_email is optional row-level ABAC on SFDC-sourced rows
  // (see opportunity_controller.py's list_opportunities/get_opportunity).
  listOpps: (page = 1, pageSize = 100) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    const viewerEmail = getViewerEmail();
    if (viewerEmail) params.append("user_email", viewerEmail);
    return api.get(`/opportunities/?${params.toString()}`).then((r) => r.data);
  },

  getOpp: (id) => {
    const viewerEmail = getViewerEmail();
    const url = viewerEmail
      ? `/opportunities/${id}?user_email=${encodeURIComponent(viewerEmail)}`
      : `/opportunities/${id}`;
    return api.get(url).then((r) => r.data);
  },

  // Templates (for draft creation) — list is id/name/contract_type only,
  // no section count or version.
  listTemplates: (contractType = "") => {
    const qs = contractType ? `?contract_type=${encodeURIComponent(contractType)}` : "";
    return api.get(`/sow-templates${qs}`).then((r) => r.data);
  },

  listTemplatesV2: (contractType = "") => sowDraftApi.listTemplates(contractType),

  // Draft CRUD
  listDrafts: (opportunityId = null) => {
    const qs = opportunityId ? `?opportunity_id=${encodeURIComponent(opportunityId)}` : "";
    return api.get(`/sow-drafts${qs}`).then((r) => r.data);
  },

  createDraft: (data) => api.post("/sow-drafts", data).then((r) => r.data),

  getDraft: (id) => api.get(`/sow-drafts/${id}`).then((r) => r.data),

  // Start full-draft AI generation — background pipeline, no request body
  // (the new backend doesn't model the wizard's resources/milestones/
  // pricing as structured input; see SowDraftPage.jsx's own note).
  generateDraft: (draftId) => api.post(`/sow-drafts/${draftId}/generate`).then((r) => r.data),

  // Poll generation status — {draft_id, status, generation_error}, no
  // fine-grained current/total/section/percent progress.
  getGenerationStatus: (draftId) =>
    api.get(`/sow-drafts/${draftId}/generation-status`).then((r) => r.data),

  // Regenerate a single section
  regenerateSection: (draftId, sectionId, force = true) =>
    api.post(`/sow-drafts/${draftId}/sections/${sectionId}/regenerate`, { force }).then((r) => r.data),

  // Section management
  updateSection: (draftId, sectionId, data) =>
    api.put(`/sow-drafts/${draftId}/sections/${sectionId}`, data).then((r) => r.data),

  addSection: (draftId, data) =>
    api.post(`/sow-drafts/${draftId}/sections`, data).then((r) => r.data),

  deleteSection: (draftId, sectionId) =>
    api.delete(`/sow-drafts/${draftId}/sections/${sectionId}`).then((r) => r.data),

  reorderSections: (draftId, orderedSectionIds) =>
    api.put(`/sow-drafts/${draftId}/sections/reorder`, { ordered_section_ids: orderedSectionIds }).then((r) => r.data),

  // Versions
  getVersions: (draftId) => api.get(`/sow-drafts/${draftId}/versions`).then((r) => r.data),

  getVersionDetail: (draftId, version) =>
    api.get(`/sow-drafts/${draftId}/versions/${encodeURIComponent(version)}`).then((r) => r.data),

  restoreVersion: (draftId, version) =>
    api.post(`/sow-drafts/${draftId}/restore`, { version }).then((r) => r.data),

  // Publish
  publishToSalesforce: (draftId) =>
    api.post(`/sow-drafts/${draftId}/publish/salesforce`).then((r) => r.data),

  publishToGoogleDocs: (draftId) =>
    api.post(`/sow-drafts/${draftId}/publish/google-docs`).then((r) => r.data),

  // Preview — no backend route on the new architecture yet; left pointing
  // at the old path deliberately (see this block's own top comment).
  preview: (draftId) => api.get(`/sow-drafts/${draftId}/preview`).then((r) => r.data),

  exportDocx: (draftId) =>
    api.get(`/sow-drafts/${draftId}/export`, { responseType: "blob" }).then((r) => r.data),

  // Proposal Document Upload & Parsing
  parseProposals: (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    return api
      .post("/sow-drafts/parse-proposals", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      })
      .then((r) => r.data);
  },

  // JOSS Resource Estimate
  fetchJossResources: (optyId) =>
    api.get(`/sow-drafts/joss/resource-estimate`, { params: { opty_id: optyId } }).then((r) => r.data),
};

// ----------------------------------------------------
// Default export (axios instance)
// ----------------------------------------------------

export default api;
